import { z } from 'zod';

export const staffStatColumnRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('grouping'), key: z.string() }),
  z.object({ kind: z.literal('stat'), alias: z.string() }),
  z.object({ kind: z.literal('display'), alias: z.string() }),
]);

export type StaffStatColumnRef = z.infer<typeof staffStatColumnRefSchema>;

const catalogKindSchema = z.enum(['grouping', 'stat', 'display']);

/** API may send `null` when no tooltip is defined. */
const nullableTooltipSchema = z
  .string()
  .nullable()
  .optional()
  .transform((value) => value ?? undefined);

export const staffStatCatalogRowSchema = z.object({
  catalogId: z.string(),
  label: z.string(),
  tooltip: nullableTooltipSchema,
  kind: catalogKindSchema,
  ref: z.string(),
  statKeys: z.array(z.string()),
  columnRef: staffStatColumnRefSchema,
  effectiveAllowByStatKey: z.record(z.string(), z.union([z.boolean(), z.null()])),
  /** Metric / detail tag headers (same semantics as stat metadata `tags`). */
  tags: z
    .array(z.string())
    .nullable()
    .optional()
    .transform((t) => t ?? []),
  /** When set on a display row, nested under the dimension grouping with this `columnRef.key`. */
  idColumnAlias: z.string().nullable().optional(),
});

export type StaffStatCatalogRow = z.infer<typeof staffStatCatalogRowSchema>;

const divergenceDetailSchema = z.object({
  catalogId: z.string(),
  kind: catalogKindSchema,
  ref: z.string(),
  byStatKey: z.record(z.string(), z.boolean()),
});

export const staffStatColumnCatalogDivergenceSchema = z.object({
  conflictingCatalogIds: z.array(z.string()),
  details: z.array(divergenceDetailSchema),
});

export type StaffStatColumnCatalogDivergence = z.infer<
  typeof staffStatColumnCatalogDivergenceSchema
>;

export const staffStatColumnCatalogResponseSchema = z.object({
  catalog: z.array(staffStatCatalogRowSchema),
  bulkEditable: z.boolean(),
  divergence: staffStatColumnCatalogDivergenceSchema.nullable(),
  /** Parent mode: client id whose ceiling constrains this account. */
  constraintAccountId: z.number().optional(),
  /** Optional API hint for metric tag section order (matches analytics stat metadata). */
  statColumnTagsSortOrder: z.array(z.string()).optional(),
  displayColumnTagsSortOrder: z.array(z.string()).optional(),
});

export type StaffStatColumnCatalogResponse = z.infer<typeof staffStatColumnCatalogResponseSchema>;

export const applyStaffStatColumnCatalogBodySchema = z
  .object({
    allowedCatalogIds: z.array(z.string().min(1)).optional(),
    statKeys: z.array(z.string().min(1).max(100)).optional(),
    /**
     * Staff client writes: Genie 2.0 mode to assign. Never `legacy`.
     * Parent_delegate: omit (API ignores body and always persists the parent's mode).
     */
    analyticsReportingMode: z.enum(['v2', 'v2_legacy']).optional(),
  })
  .strict();

export type ApplyStaffStatColumnCatalogBody = z.infer<typeof applyStaffStatColumnCatalogBodySchema>;

export const applyStaffStatColumnCatalogSuccessSchema = z.object({
  ok: z.literal(true),
  touchedStatKeys: z.array(z.string()),
});

export type ApplyStaffStatColumnCatalogSuccess = z.infer<
  typeof applyStaffStatColumnCatalogSuccessSchema
>;
